<template>
  <div
    :class="`offline-download-card bg-card-${typeColor}-light border border-color-medium rounded px-3 py-2 mb-2 cursor-pointer`"
    @click="onOpenFile"
  >
    <div :class="`colored-border rounded bg-color-${typeColor}-dark`"></div>
    <CustomImage :src="getIconUrl(cardIcon)" class="card-icon" />

    <div class="row-center card-header-wrapper justify-between mb-2">
      <div class="title-wrapper flex flex-col justify-start gap-1">
        <div :class="`text-xs font-semibold truncate color-${typeColor}`">{{ download.Name }}</div>
        <div class="row-center-gap-2">
          <ColoredTag :tagText="typeLabel" :color="typeColor" variant="soft" />
        </div>
      </div>
    </div>
    <div class="row-center card-main justify-between">
      <div class="info-grid">
        <CardInfoField
          icon="server"
          label="Size"
          :value="getFormattedFileSize(download.TotalSizeBytes)"
          :color="typeColor"
        />
        <CardInfoField
          icon="calendar-check"
          label="Downloaded"
          :value="formattedDate"
          :color="typeColor"
        />
      </div>
      <div
        class="ti ti-trash text-m color-danger cursor-pointer"
        @click.stop="onRemoveDownload"
      ></div>
    </div>
  </div>
</template>

<script lang="ts">
import CardInfoField from "@/components/cards/CardInfoField.vue";
import ColoredTag from "@/components/primitives/ColoredTag.vue";
import { OfflineDownloadTypeEnum } from "@/enums/offlineDownload";
import { getFormattedFileSize, getIconUrl } from "@/methods/utils";
import { OfflineDownloadItem } from "@/types/offlineDownload";
import { format, parseISO } from "date-fns";
import { computed, defineComponent, PropType } from "vue";
import CustomImage from "@/components/primitives/CustomImage.vue";

export default defineComponent({
  name: "OfflineDownloadCard",
  components: { CustomImage, ColoredTag, CardInfoField },
  props: { download: { type: Object as PropType<OfflineDownloadItem>, required: true } },
  emits: ["onOpen", "onRemove"],
  setup(props, { emit }) {
    const cardIcon = computed(() => {
      switch (props.download.Type) {
        case OfflineDownloadTypeEnum.PDF:
          return "open-book";
        case OfflineDownloadTypeEnum.PRESENTATION:
          return "presentation";
        case OfflineDownloadTypeEnum.IMAGE:
          return "bi-file-earmark-image";
        default:
          return "homework";
      }
    });

    const typeColor = computed(() => {
      switch (props.download.Type) {
        case OfflineDownloadTypeEnum.PDF:
          return "danger";
        case OfflineDownloadTypeEnum.PRESENTATION:
          return "primary";
        case OfflineDownloadTypeEnum.IMAGE:
          return "tertiary";
        default:
          return "secondary";
      }
    });

    const typeLabel = computed(() => {
      let label = props.download.FileCount > 1 ? `${props.download.FileCount} ` : "";
      switch (props.download.Type) {
        case OfflineDownloadTypeEnum.PRESENTATION:
          label += "Slides";
          break;
        default:
          label += props.download.Type.toString();
      }
      return label;
    });

    const formattedDate = computed(() => {
      try {
        return format(parseISO(props.download.DownloadedOn), "dd MMM yyyy");
      } catch {
        return "";
      }
    });

    function onOpenFile() {
      emit("onOpen", props.download);
    }

    function onRemoveDownload() {
      emit("onRemove", props.download.Id);
    }

    return {
      OfflineDownloadTypeEnum,
      cardIcon,
      typeColor,
      typeLabel,
      formattedDate,
      getIconUrl,
      getFormattedFileSize,
      onOpenFile,
      onRemoveDownload,
    };
  },
});
</script>

<style lang="scss" scoped>
// scoped-defer: bucket-c-candidate: nested SCSS rules — child-element gluing, hoist to @layer components if reused
.offline-download-card {
  position: relative;
  overflow: hidden;
  --icon-width: 60px;
  & .card-icon {
    width: var(--icon-width);
    position: absolute;
    top: -15px;
    right: -15px;
    width: var(--icon-width);
    height: var(--icon-width);
    opacity: 0.33;
  }
  & .card-header-wrapper {
    & .title-wrapper {
      width: calc(100% - 60px);
      z-index: 10;
    }
  }
  & .card-main {
    & .info-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.5rem;
      width: 100%;
    }
  }
}
</style>
